// Shared
import { getTextWidth } from '../shared/helper';

// Components
import Actions from '../components/listing/Actions/Actions';
import Filter from '../components/listing/Filter/Filter';
import FormInput from '../components/detail/FormInput/FormInput';

// Taxonomies
import { countryCore, countryFilter } from '../taxonomies/country';
import { statusCore, statusFilter } from '../taxonomies/status';
import { vehicleDataVersionCore, vehicleDataVersionFilter } from './vehicleDataVersion';
import { pricingDataVersionCore, pricingDataVersionFilter } from './pricingDataVersion';
import { pricingModelCore, pricingModelFilter } from './pricingModel';

// Sales Channel core
export function salesChannelCore() {
  const endpointSimple = '/saleschannels/simple';
  const endpoint = '/saleschannels';
  const detailId = 'salesChannelId';

  const getDetailId = (data = null) => {
    if (!data) {
      return;
    }

    if (Array.isArray(data)) {
      return data[0][detailId];
    }

    return data[detailId];
  };

  return {
    endpoint,
    endpointSimple,
    detailId,
    getDetailId: getDetailId
  };
}

// Sales Channel filter
export function salesChannelFilter() {
  const parameter = 'SalesChannelId';

  // Get rows
  const getRows = (data) => {
    const rows = [];

    data.forEach((row, i) => {
      // Text/numeric values
      rows[i] = {
        value: row.salesChannelId,
        label: row.salesChannelCode
      };
    });

    return rows;
  };

  return {
    parameter,
    getRows: getRows
  };
}


// Sales Channel listing
export function salesChannelListing() {
  // Initial sort
  const initialSort = { field: 'id', sort: 'asc' };

  // Initial filter values
  const initialFilters = { DescriptionSearchType: 'C' };

  // Get columns
  // columnsCustomized is used in case there is a hidden column
  const getColumns = (rows, columnsCustomized) => {
    const columns =
      columnsCustomized.length > 0
        ? columnsCustomized
        : [
            {
              field: 'actions',
              headerName: 'Actions',
              sortable: false,
              renderCell: function (params) {
                return (
                  <Actions
                    typeOfData="taxonomy"
                    id={params.row.realId}
                    endpoint="/sales-channel"
                    deleteApiEndpoint="/saleschannels"
                    //deleteApiPath={`/saleschannels/${params.row.realId}`}
                  />
                );
              },
              hideFromColumnsSelector: true
            },
            // DataGrid requires the "id" column to be unique,
            // so this is a unique/fake ID required by DataGrid
            { field: 'id', headerName: 'Unique ID', hide: true, hideFromColumnsSelector: true },
            // DataGrid requires the "id" column to be unique,
            // so this is the real ID to show on the columns for the user
            { field: 'realId', headerName: 'ID', hide: true, hideFromColumnsSelector: true },
            { field: 'code', headerName: 'Code', hide: true, hideFromColumnsSelector: true },
            { field: 'description', headerName: 'Sales Channel' },
            { field: 'statusDescription', headerName: 'Status' },
            { field: 'vehicleVersion', headerName: 'Vehicle Data Version', hideFromColumnsSelector: true },
            { field: 'pricingVersion', headerName: 'Pricing Data Version', hideFromColumnsSelector: true },
            { field: 'pricingModel', headerName: 'Pricing Model', hideFromColumnsSelector: true },
            { field: 'country', headerName: 'Country' },
            { field: 'trims', headerName: 'Available Vehicles', hideFromColumnsSelector: true },
            { field: 'exteriorColors', headerName: 'Exterior Colors', hideFromColumnsSelector: true },
            { field: 'upholsteries', headerName: 'Upholstery', hideFromColumnsSelector: true },
            { field: 'packages', headerName: 'Packages', hideFromColumnsSelector: true },
            { field: 'options', headerName: 'Options', hideFromColumnsSelector: true },
            { field: 'accessories', headerName: 'Accessories', hideFromColumnsSelector: true },
            { field: 'enabled', headerName: 'Enabled', hideFromColumnsSelector: true },
          ];

    const columnsResized = columns;

    columns.forEach((column, i) => {
      const id = column.field;
      let text = column.headerName;

      const textWidth = getTextWidth(text);
      columnsResized[i].width = textWidth;

      rows.forEach((row, j) => {
        if (row[id]) {
          const text = row[id];
          const textWidth = getTextWidth(text);

          if (columnsResized[i].width < textWidth) {
            columnsResized[i].width = textWidth;
          }
        }
      });
    });

    return columnsResized;
  };

  // Get rows
  const getRows = (data) => {
    const rows = [];

    data.forEach((row, i) => {
      // Text/numeric values
      rows[i] = {
        id: i,
        realId: row.salesChannelId,
        code: row.salesChannelCode,
        description: row.salesChannelDescription,
        enabled: row.salesChannelEnabled ? 'Yes' : 'No',
        status: row.status,
        statusDescription: row.statusDescription,
        vehicleVersionId: row.vehicleDataVersionId,
        vehicleVersion: row.vehicleDataVersionDescription,
        pricingVersionId: row.pricingDataVersionId,
        pricingVersion: row.pricingDataVersionDescription,
        pricingModel: row.salesChannelPricingModel,
        countryId: row.countryID,
        country: row.countryName,
        trims: row.salesChannelTrims,
        exteriorColors: row.salesChannelExteriorColors,
        upholsteries: row.salesChannelUpholsteries,
        packages: row.salesChannelPackages,
        options: row.salesChannelOptions,
        accessories: row.salesChannelAccessories,
      };
    });

    return rows;
  };

  // Get filters
  const getFilters = () => {
    // Taxonomies
    const countryCore_ = countryCore();
    const countryFilter_ = countryFilter();

    const statusCore_ = statusCore();
    const statusFilter_ = statusFilter();

    return [
      [
        {
          size: { xs: 12, md: 6 },
          component: (
            <Filter
              type="select"
              parameter={countryFilter_.parameter}
              label="Country"
              endpoint={countryCore_.endpoint}
              getRows={countryFilter_.getRows}
            />
          )
        },
        {
          size: { xs: 12, md: 6 },
          component: (
            <Filter
              type="select"
              parameter={statusFilter_.parameter}
              label="Status"
              endpoint={statusCore_.endpoint}
              getRows={statusFilter_.getRows}
            />
          )
        }
      ],
      [
        {
          size: { xs: 12 },
          component: (
            <Filter
              type="duo"
              parameter={['DescriptionSearchType', 'Description']}
              label="Sales Channel"
            />
          )
        }
      ]
    ];
  };

  return {
    initialSort,
    initialFilters,
    getColumns: getColumns,
    getRows: getRows,
    getFilters: getFilters
  };

}


// Sales Channel detail
export function salesChannelDetail() {
  const baseUrl = '/sales-channel';

  const getInputs = (data = null, isCountryDisabled = false) => {
    // set an object of all the field values
    // where the default state is empty for each
    let detailObj = {
      salesChannelId: '',
      salesChannelCode: '',
      salesChannelDescription: '',
      salesChannelEnabled: false,
      countryID: '',
      salesChannelVehicleVersionId: '',
      salesChannelPricingVersionId: '',
      salesChannelPricingModel: '',
    };

    if (data !== null) {
      // Use the data provided to populate the default value of the fields
      Object.entries(detailObj).forEach(([i, prop]) => {
        detailObj[i] = data[i];
      });
    }

    // Taxonomies
    const countryCore_ = countryCore();
    const countryFilter_ = countryFilter();

    const vehicleDataVersionCore_ = vehicleDataVersionCore();
    const vehicleDataVersionFilter_ = vehicleDataVersionFilter();

    const pricingDataVersionCore_ = pricingDataVersionCore();
    const pricingDataVersionFilter_ = pricingDataVersionFilter();

    const pricingModelCore_ = pricingModelCore();
    const pricingModelFilter_ = pricingModelFilter();

    return [
      [
        {
          hidden: true,
          component: (
            <FormInput type="hidden" parameter="salesChannelId" inputValue={detailObj.salesChannelId} />
          )
        }
      ],
      // Name (Description) - not a localization field, just a field!
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="text"
              parameter="salesChannelDescription"
              label="Sales Channel"
              required={true}
              inputValue={detailObj.salesChannelDescription}
            />
          )
        }
      ],
      // Sales Channel Code
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="text"
              parameter="salesChannelCode"
              label="Code"
              required={true}
              inputValue={detailObj.salesChannelCode}
            />
          )
        }
      ],
      // Enabled
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="checkbox"
              label="Enabled"
              parameter="salesChannelEnabled"
              required={true}
              inputValue={detailObj.salesChannelEnabled}
            />
          )
        }
      ],
      // Vehicle Data Version
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="select"
              parameter="salesChannelVehicleVersionId"
              label="Vehicle Data Version"
              required={true}
              endpoint={vehicleDataVersionCore_.endpoint}
              getRows={vehicleDataVersionFilter_.getRows}
              inputValue={detailObj.salesChannelVehicleVersionId}
            />
          )
        }
      ],
      // Pricing Data Version
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="select"
              parameter="salesChannelPricingVersionId"
              label="Pricing Data Version"
              required={true}
              endpoint={pricingDataVersionCore_.endpoint}
              getRows={pricingDataVersionFilter_.getRows}
              inputValue={detailObj.salesChannelPricingVersionId}
            />
          )
        }
      ],
      // Pricing Model
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="select"
              parameter="salesChannelPricingModel"
              label="Pricing Model"
              required={true}
              endpoint={pricingModelCore_.endpoint}
              getRows={pricingModelFilter_.getRows}
              inputValue={detailObj.salesChannelPricingModel}
            />
          )
        }
      ],
      // Country
      [
        {
          colSize: { xs: 12, md: 8, lg: 6 },
          size: { xs: 12 },
          component: (
            <FormInput
              type="select"
              parameter={countryCore_.detailId}
              label="Country"
              required={true}
              endpoint={countryCore_.endpoint}
              getRows={countryFilter_.getRows}
              inputValue={detailObj.countryID}
              disabled={isCountryDisabled}
            />
          )
        }
      ],
    ];
  };

  return {
    baseUrl,
    getInputs: getInputs
  };
}